import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useMyTenants } from '@/lib/api/tenants';
import { getActiveTenantId, persistActiveTenantId } from '@/lib/tenant';

export interface TenantInfo {
  id: string;
  /** The workspace URL slug (spec 0013); the leading path segment for the SPA. */
  slug: string;
  name: string;
  role: string;
}

interface TenantContextValue {
  tenants: TenantInfo[];
  activeTenantId: string | null;
  activeTenant: TenantInfo | null;
  setActiveTenant: (id: string) => void;
  isLoading: boolean;
  /** The membership query failed -- an empty list must NOT be trusted. */
  loadError: Error | null;
  reload: () => void;
}

const TenantContext = createContext<TenantContextValue | undefined>(undefined);

/**
 * Loads the tenants the user belongs to from the backend (GET /tenants/mine),
 * which is the source of truth for membership, and tracks the active tenant.
 */
export function TenantProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { data, isLoading, error, refetch } = useMyTenants();

  const tenants = useMemo<TenantInfo[]>(
    () =>
      (data ?? []).map((m) => ({
        id: m.tenant.id,
        slug: m.tenant.slug,
        name: m.tenant.name,
        role: m.role,
      })),
    [data],
  );

  const [activeTenantId, setActiveTenantId] = useState<string | null>(() => getActiveTenantId());

  // Keep the active tenant pointing at one the user actually belongs to.
  useEffect(() => {
    if (tenants.length === 0) return;
    const stillValid = activeTenantId && tenants.some((t) => t.id === activeTenantId);
    if (!stillValid) {
      const next = tenants[0].id;
      setActiveTenantId(next);
      persistActiveTenantId(next);
    }
  }, [tenants, activeTenantId]);

  const setActiveTenant = useCallback(
    (id: string) => {
      setActiveTenantId(id);
      persistActiveTenantId(id);
      // Tenant scope changed: drop all cached, tenant-scoped data.
      void queryClient.invalidateQueries();
    },
    [queryClient],
  );

  const reload = useCallback(() => {
    void refetch();
  }, [refetch]);

  const value = useMemo<TenantContextValue>(
    () => ({
      tenants,
      activeTenantId,
      activeTenant: tenants.find((t) => t.id === activeTenantId) ?? null,
      setActiveTenant,
      isLoading,
      loadError: (error as Error | null) ?? null,
      reload,
    }),
    [tenants, activeTenantId, setActiveTenant, isLoading, error, reload],
  );

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenantContext(): TenantContextValue {
  const ctx = useContext(TenantContext);
  if (!ctx) {
    throw new Error('useTenantContext must be used within a TenantProvider');
  }
  return ctx;
}
